import React, { useState, useRef } from "react";
import {
  VStack,
  HStack,
  Box,
  Text,
  Button,
  Input,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Progress,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { 
  FiUpload, 
  FiDownload, 
  FiFileText, 
  FiCheck, 
  FiX, 
  FiInfo,
  FiAlertCircle 
} from "react-icons/fi";

const BulkImportHolidays = ({
  bulkImportHolidaysPayload,
  bulkImportHolidaysIsLoading = false,
  closeModal,
  refetchGetHolidays,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef();
  const toast = useToast();

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      toast({
        title: "Invalid file type",
        description: "Please select a CSV file",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select a file smaller than 5MB",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setSelectedFile(file);
    parseCSVFile(file);
  };

  const parseCSVFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target.result;
        const lines = csv.split('\n');
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        
        // Validate headers
        const requiredHeaders = ['holiday_name', 'holiday_date', 'holiday_description', 'is_recurring'];
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        
        if (missingHeaders.length > 0) {
          toast({
            title: "Invalid CSV format",
            description: `Missing required columns: ${missingHeaders.join(', ')}`,
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
          return;
        }

        const data = [];
        const errors = [];

        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;
          
          const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
          const row = {
            holiday_name: values[headers.indexOf('holiday_name')] || '',
            holiday_date: values[headers.indexOf('holiday_date')] || '',
            holiday_description: values[headers.indexOf('holiday_description')] || '',
            is_recurring: values[headers.indexOf('is_recurring')] || 'false',
            rowNumber: i + 1,
          };

          // Validate row data
          const rowErrors = validateRow(row);
          if (rowErrors.length > 0) {
            errors.push({ row: i + 1, errors: rowErrors });
          }

          data.push(row);
        }

        setPreviewData(data);
        setValidationErrors(errors);

        if (errors.length > 0) {
          toast({
            title: "Validation errors found",
            description: `${errors.length} rows have validation errors. Please fix them before importing.`,
            status: "warning",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
        } else {
          toast({
            title: "File parsed successfully",
            description: `Found ${data.length} holidays to import`,
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
        }
      } catch (error) {
        toast({
          title: "Error parsing file",
          description: "Please check your CSV format and try again",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
    };
    reader.readAsText(file);
  };

  const validateRow = (row) => {
    const errors = [];

    // Validate holiday name
    if (!row.holiday_name.trim()) {
      errors.push("Holiday name is required");
    } else if (row.holiday_name.trim().length < 2) {
      errors.push("Holiday name must be at least 2 characters");
    }

    // Validate holiday date
    if (!row.holiday_date) {
      errors.push("Holiday date is required");
    } else {
      const date = new Date(row.holiday_date);
      if (isNaN(date.getTime())) {
        errors.push("Invalid date format (use YYYY-MM-DD)");
      } else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (date < today) {
          errors.push("Holiday date cannot be in the past");
        }
      }
    }

    // Validate description length
    if (row.holiday_description && row.holiday_description.length > 500) {
      errors.push("Description must be less than 500 characters");
    }

    // Validate recurring field
    const recurringValue = row.is_recurring.toLowerCase();
    if (!['true', 'false', '1', '0', 'yes', 'no'].includes(recurringValue)) {
      errors.push("is_recurring must be true/false, 1/0, or yes/no");
    }

    return errors;
  };

  const handleUpload = async () => {
    if (validationErrors.length > 0) {
      toast({
        title: "Cannot import",
        description: "Please fix all validation errors before importing",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    if (previewData.length === 0) {
      toast({
        title: "No data to import",
        description: "Please select a valid CSV file",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setIsProcessing(true);
    setUploadProgress(0);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Prepare data for API
      const importData = previewData.map(row => ({
        holiday_name: row.holiday_name.trim(),
        holiday_date: row.holiday_date,
        holiday_description: row.holiday_description.trim(),
        is_recurring: ['true', '1', 'yes'].includes(row.is_recurring.toLowerCase()),
      }));

      await bulkImportHolidaysPayload(importData);
      
      setUploadProgress(100);
      clearInterval(progressInterval);

      // Reset form
      setSelectedFile(null);
      setPreviewData([]);
      setValidationErrors([]);
      setUploadProgress(0);

      // Close modal and refresh
      closeModal();
      if (refetchGetHolidays) {
        refetchGetHolidays();
      }

      toast({
        title: "Import successful",
        description: `Successfully imported ${importData.length} holidays`,
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      setUploadProgress(0);
      toast({
        title: "Import failed",
        description: error.message || "Failed to import holidays",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadTemplate = () => {
    const template = `holiday_name,holiday_date,holiday_description,is_recurring
"Christmas Day","2025-12-25","Christmas celebration",true
"New Year's Day","2025-01-01","New Year celebration",true
"Independence Day","2025-07-04","Independence Day celebration",true
"Thanksgiving Day","2025-11-27","Thanksgiving celebration",true
"Company Anniversary","2025-06-15","Company founding anniversary",false`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'holidays_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast({
      title: "Template downloaded",
      description: "CSV template has been downloaded",
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top",
    });
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setPreviewData([]);
    setValidationErrors([]);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getRowStatus = (rowNumber) => {
    const hasErrors = validationErrors.some(error => error.row === rowNumber);
    return hasErrors ? 'error' : 'valid';
  };

  const isLoading = bulkImportHolidaysIsLoading || isProcessing;

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Box>
        <Text fontSize="lg" fontWeight="600" color="gray.900">
          Bulk Import Holidays
        </Text>
        <Text fontSize="sm" color="gray.600" mt={1}>
          Upload a CSV file to import multiple holidays at once
        </Text>
      </Box>

      {/* Instructions */}
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Box>
          <AlertTitle>Instructions</AlertTitle>
          <AlertDescription>
            Download the template, fill in your holiday data, and upload the CSV file. 
            The file should include: holiday_name, holiday_date, holiday_description, and is_recurring columns.
          </AlertDescription>
        </Box>
      </Alert>

      {/* Template Download */}
      <Box
        bg="blue.50"
        p={4}
        borderRadius="md"
        border="1px solid"
        borderColor="blue.200"
      >
        <HStack justify="space-between" align="center">
          <HStack spacing={3}>
            <FiFileText color="blue.600" />
            <Box>
              <Text fontWeight="500" color="blue.900">CSV Template</Text>
              <Text fontSize="sm" color="blue.700">
                Download the template to see the required format
              </Text>
            </Box>
          </HStack>
          <Button
            leftIcon={<FiDownload />}
            colorScheme="blue"
            variant="outline"
            size="sm"
            onClick={handleDownloadTemplate}
          >
            Download Template
          </Button>
        </HStack>
      </Box>

      {/* File Upload */}
      <Box
        border="2px dashed"
        borderColor={selectedFile ? "green.300" : "gray.300"}
        borderRadius="md"
        p={6}
        textAlign="center"
        bg={selectedFile ? "green.50" : "gray.50"}
        transition="all 0.2s"
      >
        {!selectedFile ? (
          <VStack spacing={4}>
            <FiUpload size={48} color="gray.400" />
            <Box>
              <Text fontWeight="500" color="gray.700">
                Choose CSV file or drag and drop
              </Text>
              <Text fontSize="sm" color="gray.500" mt={1}>
                Maximum file size: 5MB
              </Text>
            </Box>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              display="none"
            />
            <Button
              colorScheme="blue"
              onClick={() => fileInputRef.current?.click()}
            >
              Select File
            </Button>
          </VStack>
        ) : (
          <VStack spacing={4}>
            <FiCheck size={48} color="green.500" />
            <Box>
              <Text fontWeight="500" color="green.700">
                {selectedFile.name}
              </Text>
              <Text fontSize="sm" color="green.600">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </Text>
            </Box>
            <HStack spacing={2}>
              <Button
                leftIcon={<FiX />}
                variant="outline"
                size="sm"
                onClick={handleClearFile}
              >
                Remove
              </Button>
            </HStack>
          </VStack>
        )}
      </Box>

      {/* Progress Bar */}
      {isProcessing && (
        <Box>
          <Text fontSize="sm" color="gray.600" mb={2}>
            Uploading holidays...
          </Text>
          <Progress value={uploadProgress} colorScheme="blue" borderRadius="md" />
        </Box>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Validation Errors</AlertTitle>
            <AlertDescription>
              {validationErrors.length} rows have errors that need to be fixed before importing.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Preview Table */}
      {previewData.length > 0 && (
        <Box>
          <HStack justify="space-between" align="center" mb={4}>
            <Text fontWeight="500" color="gray.700">
              Preview ({previewData.length} holidays)
            </Text>
            <Badge
              colorScheme={validationErrors.length > 0 ? "red" : "green"}
              variant="subtle"
            >
              {validationErrors.length > 0 ? `${validationErrors.length} errors` : "Valid"}
            </Badge>
          </HStack>
          
          <Box
            maxH="300px"
            overflowY="auto"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
          >
            <Table variant="simple" size="sm">
              <Thead bg="gray.50" position="sticky" top={0}>
                <Tr>
                  <Th>Row</Th>
                  <Th>Holiday Name</Th>
                  <Th>Date</Th>
                  <Th>Description</Th>
                  <Th>Recurring</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {previewData.slice(0, 10).map((row, index) => (
                  <Tr key={index}>
                    <Td fontSize="sm">{row.rowNumber}</Td>
                    <Td fontWeight="500">{row.holiday_name}</Td>
                    <Td>{row.holiday_date}</Td>
                    <Td>
                      <Text noOfLines={2} fontSize="sm">
                        {row.holiday_description || "-"}
                      </Text>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={row.is_recurring === 'true' ? "green" : "blue"}
                        variant="subtle"
                        size="sm"
                      >
                        {row.is_recurring === 'true' ? "Yes" : "No"}
                      </Badge>
                    </Td>
                    <Td>
                      {getRowStatus(row.rowNumber) === 'error' ? (
                        <Tooltip label="Click to see errors">
                          <IconButton
                            icon={<FiAlertCircle />}
                            colorScheme="red"
                            variant="ghost"
                            size="sm"
                            aria-label="View errors"
                          />
                        </Tooltip>
                      ) : (
                        <FiCheck color="green" />
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          
          {previewData.length > 10 && (
            <Text fontSize="sm" color="gray.500" mt={2} textAlign="center">
              Showing first 10 rows of {previewData.length} total
            </Text>
          )}
        </Box>
      )}

      <Divider />

      {/* Action Buttons */}
      <HStack spacing={3} justify="flex-end">
        <Button
          leftIcon={<FiX />}
          variant="outline"
          onClick={closeModal}
          isDisabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          leftIcon={<FiUpload />}
          colorScheme="blue"
          onClick={handleUpload}
          isLoading={isLoading}
          loadingText="Importing..."
          isDisabled={!selectedFile || previewData.length === 0 || validationErrors.length > 0}
        >
          Import Holidays
        </Button>
      </HStack>
    </VStack>
  );
};

export default BulkImportHolidays; 